import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { makeStyles, Snackbar, Tooltip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import SydButton from '../../../../../commonDesign/Button'
import Loading from '../../../../../molecules/Loading'
import ErrorDisplay from '../../../../../molecules/ErrorDisplay'
import { dataPipelineVersioning } from '../../../../../../api/coreData'
import { formatRelativeTime } from '../../SmartEndpoints/formatRelativeTime'
import { ADMIN_ROUTES } from '../../../../../../constants'
import DataPipelineEditorProvider, { useEditor } from './editor/EditorContext'
import JsonPanel from './editor/JsonPanel'
import TestPanel from './editor/TestPanel'
import PipelineChatPanel from './editor/PipelineChatPanel'
import ProposalDiffModal from './editor/ProposalDiffModal'

const SCOPE_OPTIONS = ['system', 'account', 'client', 'asset']

const NEW_INITIAL = {
  code: '',
  displayName: '',
  description: '',
  scope: 'client',
  pipeline: { components: [], variables: { filters: {}, dates: {} } },
  analysis: null
}

const useStyles = makeStyles(() => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 120px)',
    overflow: 'hidden'
  },
  headerBar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 'var(--gap-md)',
    padding: 'var(--padding-sm) var(--padding-md)',
    margin: 'var(--margin-md) var(--margin-lg) 0',
    borderRadius: 'var(--border-radius-lg)',
    border: '1px solid var(--color-neutral-300)',
    backgroundColor: 'var(--color-neutral-100)',
    flexShrink: 0,
    flexWrap: 'wrap'
  },
  headerField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  fieldLabel: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 'var(--text-weight-bold)',
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingLeft: '2px'
  },
  fieldInput: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    '&:focus': { borderColor: 'var(--color-primary-400)', boxShadow: '0 0 0 2px var(--color-primary-100)' },
    '&::placeholder': { color: 'var(--color-neutral-400)' },
    '&:read-only': { backgroundColor: 'var(--color-neutral-050)', color: 'var(--color-neutral-500)' }
  },
  inputMono: {
    fontFamily: 'var(--font-mono, monospace)'
  },
  versionSelect: {
    padding: '6px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-sm)',
    outline: 'none',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)',
    cursor: 'pointer',
    '&:focus': { borderColor: 'var(--color-primary-400)', boxShadow: '0 0 0 2px var(--color-primary-100)' }
  },
  tagBadgeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '1px'
  },
  tagBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: '10px',
    fontWeight: 600,
    fontFamily: 'var(--font-mono, monospace)',
    letterSpacing: '0.2px',
    backgroundColor: 'var(--color-neutral-100)',
    color: 'var(--color-neutral-500)'
  },
  tagBadgeLive: {
    backgroundColor: 'var(--color-success-050, #f0fdf4)',
    color: 'var(--color-success-700, #15803d)'
  },
  tagBadgeNext: {
    backgroundColor: 'var(--color-primary-050, #eff6ff)',
    color: 'var(--color-primary-700, #1d4ed8)'
  },
  spacer: { flex: 1 },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-sm)',
    paddingBottom: '2px'
  },
  body: {
    display: 'flex',
    flex: 1,
    gap: 'var(--gap-lg)',
    padding: 'var(--padding-md) var(--padding-lg) 0',
    minHeight: 0,
    overflow: 'hidden'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '3px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white'
  },
  leftColumn: {
    flex: 1,
    minWidth: '380px'
  },
  middleColumn: {
    flex: 1,
    minWidth: '340px'
  },
  rightColumn: {
    width: '320px',
    flexShrink: 0,
    backgroundColor: 'var(--color-ai-050)',
    borderColor: 'var(--color-ai-200, rgba(169,88,255,0.25))'
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--gap-lg)',
    padding: '5px var(--padding-lg)',
    flexShrink: 0,
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    borderTop: '1px solid var(--color-neutral-200)'
  },
  statusDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    marginRight: '4px',
    verticalAlign: 'middle'
  }
}))

function buildInitialFromVersion (versionData) {
  if (!versionData?.version) return null
  const v = versionData.version
  return {
    code: v.code || '',
    displayName: v.displayName || '',
    description: v.description || '',
    scope: v.scope || 'client',
    pipeline: {
      components: v.pipeline?.components || [],
      variables: v.pipeline?.variables || { filters: {}, dates: {} }
    },
    analysis: v.analysis || null
  }
}

function HeaderBar ({
  isNew,
  versions,
  currentVersion,
  tagsOnCurrent,
  onSelectVersion,
  onSave,
  onReset,
  saving
}) {
  const classes = useStyles()
  const editor = useEditor()
  const saveDisabled = !editor.code || !!editor.pipelineParse.error || saving || !editor.isDirty

  return (
    <div className={classes.headerBar}>
      <div className={classes.headerField} style={{ width: 160 }}>
        <label className={classes.fieldLabel}>Code</label>
        <input
          className={`${classes.fieldInput} ${classes.inputMono}`}
          value={editor.code}
          onChange={(e) => editor.setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
          readOnly={!isNew}
          placeholder='pipeline-code'
          maxLength={64}
          style={{ width: '100%' }}
        />
      </div>

      <div className={classes.headerField} style={{ minWidth: 180, flex: 1 }}>
        <label className={classes.fieldLabel}>Name</label>
        <input
          className={classes.fieldInput}
          value={editor.displayName}
          onChange={(e) => editor.setDisplayName(e.target.value)}
          placeholder='Pipeline name'
          maxLength={64}
          style={{ width: '100%' }}
        />
      </div>

      <div className={classes.headerField} style={{ width: 130 }}>
        <label className={classes.fieldLabel}>Scope</label>
        <select
          className={classes.versionSelect}
          value={editor.scope || ''}
          onChange={(e) => editor.setScope(e.target.value)}
        >
          {SCOPE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className={classes.headerField} style={{ flex: 2, minWidth: 220 }}>
        <label className={classes.fieldLabel}>Description</label>
        <input
          className={classes.fieldInput}
          value={editor.description}
          onChange={(e) => editor.setDescription(e.target.value)}
          placeholder='Pipeline description'
          style={{ width: '100%' }}
        />
      </div>

      {!isNew && versions.length > 0 && (
        <div className={classes.headerField}>
          <label className={classes.fieldLabel}>Version</label>
          <select
            className={classes.versionSelect}
            value={currentVersion || ''}
            onChange={(e) => onSelectVersion(parseInt(e.target.value, 10))}
          >
            {versions.map(v => (
              <option key={v.version} value={v.version}>v{v.version}</option>
            ))}
          </select>
        </div>
      )}

      <div className={classes.headerField}>
        {!isNew && tagsOnCurrent.length > 0 && (
          <div className={classes.tagBadgeWrap}>
            {tagsOnCurrent.map(t => (
              <Tooltip key={t} title={t === 'next' ? 'This is the latest version' : `Tag "${t}" points to this version`} arrow>
                <span className={`${classes.tagBadge} ${t === 'live' ? classes.tagBadgeLive : ''} ${t === 'next' ? classes.tagBadgeNext : ''}`}>
                  {t}
                </span>
              </Tooltip>
            ))}
          </div>
        )}
        <div className={classes.headerActions}>
          <SydButton size='sm' variant='secondary' onClick={onReset} disabled={!editor.isDirty || saving}>Reset</SydButton>
          <SydButton size='sm' variant='primary' icon='check' onClick={onSave} disabled={saveDisabled} processing={saving}>
            Save
          </SydButton>
        </div>
      </div>
    </div>
  )
}

HeaderBar.propTypes = {
  isNew: PropTypes.bool.isRequired,
  versions: PropTypes.array.isRequired,
  currentVersion: PropTypes.number,
  tagsOnCurrent: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectVersion: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  saving: PropTypes.bool
}

function StatusBar ({ version, lastSaved, code }) {
  const classes = useStyles()
  const { isDirty } = useEditor()
  return (
    <div className={classes.statusBar}>
      <span>
        <span
          className={classes.statusDot}
          style={{ backgroundColor: isDirty ? 'var(--color-warning-400)' : 'var(--color-success-500)' }}
        />
        {isDirty ? 'Unsaved changes' : 'Saved'}
      </span>
      {version != null && <span>Version {version}</span>}
      {lastSaved && <span>Last saved {lastSaved}</span>}
      <span style={{ marginLeft: 'auto' }}>
        Data Pipeline &middot; {code || 'no code'}
      </span>
    </div>
  )
}

StatusBar.propTypes = {
  version: PropTypes.number,
  lastSaved: PropTypes.string,
  code: PropTypes.string
}

function EditorBody ({
  isNew,
  codeParam,
  versionData,
  versionsList,
  onSelectVersion,
  previewVersion
}) {
  const classes = useStyles()
  const history = useHistory()
  const editor = useEditor()
  const [toast, setToast] = useState(null)

  const saveMutation = dataPipelineVersioning.useSaveDataPipeline()

  const tags = useMemo(() => versionData?.tags || [], [versionData])
  const maxVersion = versionData?.maxVersion ?? null
  const currentVersion = versionData?.version?.version ?? null

  const versions = useMemo(() => {
    if (!versionsList?.versions) return []
    return [...versionsList.versions].sort((a, b) => b.version - a.version)
  }, [versionsList])

  const tagsOnCurrent = useMemo(() => {
    const result = tags.filter(t => t.version === currentVersion).map(t => t.tagName)
    if (currentVersion != null && currentVersion === maxVersion) result.push('next')
    return result
  }, [tags, currentVersion, maxVersion])

  const handleReset = useCallback(() => {
    if (isNew) {
      editor.setCode('')
      editor.setDisplayName('')
      editor.setDescription('')
      editor.setScope('client')
      editor.setPipelineText('[]')
      editor.setVariables({ filters: {}, dates: {} })
      editor.setAnalysis(null)
      return
    }
    const initial = buildInitialFromVersion(versionData)
    if (!initial) return
    editor.setCode(initial.code)
    editor.setDisplayName(initial.displayName)
    editor.setDescription(initial.description)
    editor.setScope(initial.scope)
    editor.setPipelineText(JSON.stringify(initial.pipeline.components, null, 2))
    editor.setVariables(initial.pipeline.variables)
    editor.setAnalysis(initial.analysis || null)
  }, [isNew, versionData, editor])

  const handleSave = useCallback(async () => {
    if (editor.pipelineParse.error) {
      setToast({ severity: 'error', message: `Pipeline JSON is invalid: ${editor.pipelineParse.error}` })
      return
    }
    if (!editor.code) {
      setToast({ severity: 'error', message: 'Code is required' })
      return
    }
    try {
      const components = Array.isArray(editor.pipelineParse.value)
        ? editor.pipelineParse.value
        : (editor.pipelineParse.value?.components || [])
      const result = await saveMutation.mutateAsync({
        code: editor.code,
        displayName: editor.displayName || editor.code,
        description: editor.description || null,
        scope: editor.scope || null,
        pipeline: { components, variables: editor.variables },
        analysis: editor.analysis
      })
      editor.markSaved()
      setToast({
        severity: 'success',
        message: result.versionCreated ? `Saved as v${result.version}` : `Metadata updated on v${result.version}`
      })
      if (isNew && result.code) {
        history.replace(`${ADMIN_ROUTES.DENALI.ANALYSIS.DATA_PIPELINES}/${result.code}`)
      } else if (result.versionCreated && previewVersion != null) {
        // Save from older version forks a new latest; jump to it.
        onSelectVersion(null)
      }
    } catch (err) {
      setToast({ severity: 'error', message: err?.response?.data?.message || err.message || 'Save failed' })
    }
  }, [editor, isNew, history, saveMutation, previewVersion, onSelectVersion])

  const lastSaved = versionData?.version?.modifiedDate
    ? formatRelativeTime(versionData.version.modifiedDate)
    : null

  return (
    <>
      <HeaderBar
        isNew={isNew}
        versions={versions}
        currentVersion={currentVersion}
        tagsOnCurrent={tagsOnCurrent}
        onSelectVersion={onSelectVersion}
        onSave={handleSave}
        onReset={handleReset}
        saving={saveMutation.isLoading}
      />

      <div className={classes.body}>
        <div className={`${classes.column} ${classes.leftColumn}`}>
          <JsonPanel />
        </div>
        <div className={`${classes.column} ${classes.middleColumn}`}>
          <TestPanel />
        </div>
        <div className={`${classes.column} ${classes.rightColumn}`}>
          <PipelineChatPanel />
        </div>
      </div>

      <StatusBar code={codeParam || editor.code || 'new'} version={currentVersion ?? undefined} lastSaved={lastSaved} />

      <ProposalDiffModal
        open={editor.proposedPipelineText != null}
        currentText={editor.pipelineText}
        proposedText={editor.proposedPipelineText || ''}
        onAccept={editor.acceptProposal}
        onReject={editor.rejectProposal}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {toast && (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant='filled' elevation={6}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </>
  )
}

EditorBody.propTypes = {
  isNew: PropTypes.bool.isRequired,
  codeParam: PropTypes.string,
  versionData: PropTypes.object,
  versionsList: PropTypes.object,
  onSelectVersion: PropTypes.func.isRequired,
  previewVersion: PropTypes.number
}

function EditDataPipeline () {
  const classes = useStyles()
  const { code: codeParam } = useParams()
  const location = useLocation()
  const isNew = !codeParam
  const [previewVersion, setPreviewVersion] = useState(null)

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const duplicateFrom = isNew ? searchParams.get('duplicateFrom') : null
  const duplicateName = isNew ? searchParams.get('name') : null

  // When duplicating we fetch the source pipeline's latest version to use as a template.
  const fetchCode = isNew ? duplicateFrom : codeParam
  const fetchEnabled = !!fetchCode

  const { data: versionData, error: versionError } =
    dataPipelineVersioning.useGetDataPipelineVersion(
      { code: fetchCode, version: !isNew ? (previewVersion || undefined) : undefined },
      { enabled: fetchEnabled }
    )
  const { data: versionsList } = dataPipelineVersioning.useListDataPipelineVersions(codeParam, { enabled: !isNew })

  const initialValue = useMemo(() => {
    if (isNew && !duplicateFrom) return NEW_INITIAL
    const built = buildInitialFromVersion(versionData)
    if (!built) return null
    if (isNew && duplicateFrom) {
      return {
        ...built,
        code: '',
        displayName: duplicateName || (built.displayName ? `${built.displayName} (copy)` : '')
      }
    }
    return built
  }, [isNew, duplicateFrom, duplicateName, versionData])

  const onSelectVersion = useCallback((v) => {
    setPreviewVersion(v === versionData?.maxVersion ? null : v)
  }, [versionData])

  if (versionError) return <ErrorDisplay error={versionError} />
  if (fetchEnabled && !initialValue) return <Loading />

  // Bake the loaded version into the key when tracking "latest" so post-save
  // refetches force a clean remount instead of reusing stale cached state.
  // For duplicate mode, key on the source code+version so the provider remounts
  // once the source data resolves.
  const providerKey = isNew
    ? duplicateFrom
      ? `new:duplicate:${duplicateFrom}:${versionData?.version?.version ?? 'pending'}`
      : 'new'
    : previewVersion != null
      ? `${codeParam}:${previewVersion}`
      : `${codeParam}:latest:${versionData?.version?.version ?? 'pending'}`

  return (
    <div className={classes.page}>
      <DataPipelineEditorProvider key={providerKey} initialValue={initialValue}>
        <EditorBody
          isNew={isNew}
          codeParam={codeParam}
          versionData={isNew ? null : versionData}
          versionsList={versionsList}
          onSelectVersion={onSelectVersion}
          previewVersion={previewVersion}
        />
      </DataPipelineEditorProvider>
    </div>
  )
}

export default EditDataPipeline
